import React from 'react';
import useUserAuth from '../hooks/useUserAuth';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Security from '@mui/icons-material/Security';
import CircularProgress from '@mui/material/CircularProgress';

const AccessDenied = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      textAlign: 'center',
    }}
  >
    <Security sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
    <Typography variant='h4' sx={{ mb: 2, color: 'error.main' }}>
      Access Denied
    </Typography>
    <Typography variant='body1' color='text.secondary'>
      This page requires administrator privileges to access.
    </Typography>
    <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
      Please contact your system administrator if you believe you should have access.
    </Typography>
  </Box>
);

const LoadingAuth = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
    }}
  >
    <CircularProgress sx={{ mb: 2 }} />
    <Typography variant='body1' color='text.secondary'>
      Verifying administrator access...
    </Typography>
  </Box>
);

/**
 * AdminRoute component that protects admin-only pages
 * Only allows access to users with admin privileges
 */
const AdminRoute = ({ children }) => {
  const { isAdmin, userLoading, isAuthenticated } = useUserAuth();

  // Development mode override - always allow access
  if (import.meta.env.DEV) {
    console.log('🚀 AdminRoute: Development mode - admin access granted');
    return children;
  }

  // Show loading while checking authentication
  if (userLoading || !isAuthenticated) {
    return <LoadingAuth />;
  }

  // Show access denied if not admin
  if (!isAdmin) {
    return <AccessDenied />;
  }

  // Render the protected component for admin users
  return children;
};

export default AdminRoute;
